/**
 * CommentsSection
 *
 * Root component for the coach↔athlete comments thread on an evaluation.
 *
 * - Polls the comment list every 30 seconds for new comments.
 * - Renders a thread of CommentThread components.
 * - Provides a CommentComposer at the bottom for new root comments.
 * - Reply mode: clicking "Reply" on a comment sets the active reply target;
 *   the composer switches to reply mode.
 * - Handles edit/delete/report by calling the corresponding API functions
 *   and refreshing the list.
 *
 * Phase 2 additions (B-2025-0020):
 * - Fetches mentionable users via useMentionableUsers (channel members) and
 *   passes the list to CommentComposer for @-mention autocomplete.
 * - `channelSlugs` prop scopes the member lookup to the relevant channels.
 *   When omitted, all user channels are searched (fallback behaviour).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  listComments,
  createComment,
  updateComment,
  deleteComment,
  reportComment,
  type EvaluationComment,
  type CommentVisibility,
} from '@/services/commentApi';
import { useMentionableUsers } from '@/hooks/useMentionableUsers';
import CommentThread from './CommentThread';
import CommentComposer from './CommentComposer';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CommentsSectionProps {
  evaluationId: string;
  /**
   * Visibility level of the underlying template.
   * When 'channel', the composer shows the channel visibility option.
   * Default: 'private' (safest default — hides channel option).
   */
  templateVisibility?: 'private' | 'channel' | 'public';
  /**
   * Channel slugs to restrict @-mention autocomplete.
   * Typically the slug of the channel this template belongs to.
   * When omitted, all user channels are searched.
   */
  channelSlugs?: string[];
  /** Optional seek callback forwarded to CommentItem timestamp chips. */
  onSeekToTimestamp?: (sec: number) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Poll interval for new comments (30 s). */
const POLL_INTERVAL_MS = 30_000;

// ─── Component ────────────────────────────────────────────────────────────────

export default function CommentsSection({
  evaluationId,
  templateVisibility = 'private',
  channelSlugs,
  onSeekToTimestamp,
}: CommentsSectionProps) {
  const { user } = useAuth();

  const [comments, setComments] = useState<EvaluationComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** Currently active reply target (root comment being replied to). */
  const [replyTarget, setReplyTarget] = useState<EvaluationComment | null>(null);

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Cancel-safe guard — set to false on unmount so that in-flight fetches
   * (initial load or 30s poll) never call setState after teardown.
   * Uses a ref (not a closure-local flag) because fetchComments is a
   * useCallback shared with action handlers; the ref persists across renders.
   */
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Mentionable users for @-mention autocomplete (Phase 2).
  const { data: mentionableUsers = [] } = useMentionableUsers(
    channelSlugs ? { channelSlugs } : {}
  );

  // ─── Fetch ─────────────────────────────────────────────────────────────────

  const fetchComments = useCallback(async () => {
    try {
      const data = await listComments(evaluationId);
      if (!mountedRef.current) return;
      setComments(data);
      setFetchError(null);
    } catch {
      // Don't wipe existing comments on a failed poll.
      if (!mountedRef.current) return;
      setFetchError('Could not load comments.');
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [evaluationId]);

  useEffect(() => {
    void fetchComments();

    // 30s polling.
    pollTimerRef.current = setInterval(() => {
      void fetchComments();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current !== null) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [fetchComments]);

  // ─── Actions ───────────────────────────────────────────────────────────────

  const handleSubmit = async (
    body: string,
    visibility: CommentVisibility,
    parentCommentId?: string | null
  ) => {
    setIsSubmitting(true);
    try {
      await createComment(evaluationId, { body, visibility, parent_comment_id: parentCommentId });
      setReplyTarget(null);
      await fetchComments();
    } catch {
      // Let the UI show the error inline in the future (MVP: silent fail).
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (comment: EvaluationComment, newBody: string) => {
    await updateComment(comment.id, { body: newBody });
    await fetchComments();
  };

  const handleDelete = async (commentId: string) => {
    await deleteComment(commentId);
    await fetchComments();
  };

  const handleReportWithReason = async (commentId: string, reason: string) => {
    await reportComment(commentId, { reason });
    // No UI feedback needed for MVP.
  };

  const handleReply = (comment: EvaluationComment) => {
    setReplyTarget(comment);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const templateIsChannelVisible = templateVisibility === 'channel';
  const currentUserId = user?.id ?? null;
  const currentUserRole = user?.role ?? null;

  return (
    <div className="space-y-2">
      {/* Loading state */}
      {isLoading && (
        <p className="text-sm text-text-muted py-4 text-center">
          Loading comments…
        </p>
      )}

      {/* Error state */}
      {!isLoading && fetchError && (
        <p className="text-sm text-danger py-2">{fetchError}</p>
      )}

      {/* Empty state */}
      {!isLoading && !fetchError && comments.length === 0 && (
        <p className="text-sm text-text-muted py-4 text-center">
          No comments yet. Be the first to leave one.
        </p>
      )}

      {/* Comment threads */}
      {!isLoading && comments.length > 0 && (
        <div className="rounded-lg border border-border-subtle bg-dark-elevated-1 divide-y divide-border-subtle">
          {comments.map((comment) => (
            <div key={comment.id} className="px-4 py-1">
              <CommentThread
                comment={comment}
                currentUserId={currentUserId}
                currentUserRole={currentUserRole}
                onReply={handleReply}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onReport={(commentId) => {
                  // Report is handled inline in CommentItem; this is a no-op hook
                  // for future centralized moderation modal.
                  void handleReportWithReason(commentId, 'User-reported content');
                }}
                onSeekToTimestamp={onSeekToTimestamp}
              />
            </div>
          ))}
        </div>
      )}

      {/* Composer — always visible for authenticated users */}
      {user && (
        <CommentComposer
          isSubmitting={isSubmitting}
          replyingToId={replyTarget?.id ?? null}
          replyingToAuthorName={replyTarget?.author_display_name ?? null}
          onCancelReply={() => setReplyTarget(null)}
          templateIsChannelVisible={templateIsChannelVisible}
          mentionableUsers={mentionableUsers}
          onSubmit={handleSubmit}
        />
      )}

      {/* Unauthenticated placeholder */}
      {!user && (
        <p className="text-xs text-text-muted py-2 text-center">
          Sign in to leave a comment.
        </p>
      )}
    </div>
  );
}
